import { DataTable, TableMenu } from '@/components'
import type { TableMenuItem } from '@/components'
import { cn } from '@/lib/utils'
import type { VehicleServicePlanNode } from '@/modules/vehicles'
import { PERMISSIONS, usePermissions } from '@/utils'
import { formatCost, formatTimeSpent } from '@/modules/vehicles/util'
import { format, formatDistanceStrict } from '@/utils/date-fns'
import { createColumnHelper } from '@tanstack/react-table'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo } from 'react'
import { Link } from 'react-router'
import { DynamicTable } from '@/modules/dynamic_tables'
import {
  MENU_COLUMN_ID,
  resolveVehicleColumns,
  useVehicleColumnLabels,
  useVehicleTableColumns,
  VehicleColumn,
} from '@/modules/vehicles/table-config'
import { ServicePlanStatusBadge } from './service-plan-status'

interface Props {
  servicePlans: VehicleServicePlanNode[]
  loading?: boolean
  /** Off on a single vehicle's own tab, where every row is that vehicle. */
  showVehicle?: boolean
  onEdit(servicePlan: VehicleServicePlanNode): void
  onDelete(servicePlan: VehicleServicePlanNode): void
  onMoveToExpirations(servicePlan: VehicleServicePlanNode): void
}

export const VehicleServicePlansTable: React.FC<Props> = ({
  servicePlans,
  loading = false,
  showVehicle = true,
  onEdit,
  onDelete,
  onMoveToExpirations,
}) => {
  const translations = useTranslate({
    noServices: 'vehicles.no-vehicle-services',
    edit: 'common.edit',
    delete: 'common.delete',
    moveToExpirations: 'vehicles.services.move-to-expirations',
    items: ['vehicles.services.num-services', { n: 0 }],
  })

  const { hasPermissions } = usePermissions()
  // Services reuse the expiration permissions: the two lived in one table until
  // FD-571, so nobody had to be re-granted anything by the split.
  const canEdit = hasPermissions(
    PERMISSIONS.vehicles.change.vehicleexpiration,
    true
  )
  const canDelete = hasPermissions(
    PERMISSIONS.vehicles.delete.vehicleexpiration,
    true
  )
  const canAdd = hasPermissions(
    PERMISSIONS.vehicles.add.vehicleexpiration,
    true
  )

  const { config, loading: configLoading } = useVehicleTableColumns(
    DynamicTable.VEHICLE_SERVICES
  )
  const labels = useVehicleColumnLabels()[DynamicTable.VEHICLE_SERVICES]

  const column = createColumnHelper<VehicleServicePlanNode>()

  const columnDefs = useMemo(() => {
    const defs = [
      column.accessor('vehicle', {
        id: VehicleColumn.VEHICLE,
        header: labels[VehicleColumn.VEHICLE],
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.vehicle?.registrationNumber ?? '').localeCompare(
            b.original.vehicle?.registrationNumber ?? ''
          ),
        cell(info) {
          const vehicle = info.getValue()
          if (!vehicle) return '-'

          return (
            <Link className="text-inherit" to={`/vehicle-fleet/${vehicle.id}`}>
              {vehicle.registrationNumber || '-'}
            </Link>
          )
        },
      }),
      column.accessor('name', {
        id: VehicleColumn.NAME,
        header: labels[VehicleColumn.NAME],
        enableSorting: true,
        cell: info => info.getValue() || '-',
      }),
      column.accessor('serviceType', {
        id: VehicleColumn.TYPE,
        header: labels[VehicleColumn.TYPE],
        cell: info => info.getValue()?.name || '-',
      }),
      column.accessor('scheduledStart', {
        id: VehicleColumn.SCHEDULED_START,
        header: labels[VehicleColumn.SCHEDULED_START],
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          new Date(a.original.scheduledStart).getTime() -
          new Date(b.original.scheduledStart).getTime(),
        cell(info) {
          const scheduledStart = info.getValue()
          if (!scheduledStart) return '-'
          return format(new Date(scheduledStart), 'PPp')
        },
      }),
      column.accessor('scheduledEnd', {
        id: VehicleColumn.SCHEDULED_END,
        header: labels[VehicleColumn.SCHEDULED_END],
        meta: { narrow: true },
        cell(info) {
          const scheduledEnd = info.getValue()
          if (!scheduledEnd) return '-'
          return format(new Date(scheduledEnd), 'PPp')
        },
      }),
      column.display({
        id: VehicleColumn.STATUS,
        header: labels[VehicleColumn.STATUS],
        meta: { narrow: true },
        cell: info => (
          <ServicePlanStatusBadge status={info.row.original.status} />
        ),
      }),
      column.accessor('workshopName', {
        id: VehicleColumn.WORKSHOP_NAME,
        header: labels[VehicleColumn.WORKSHOP_NAME],
        cell: info => info.getValue() || '-',
      }),
      column.accessor('cost', {
        id: VehicleColumn.COST,
        header: labels[VehicleColumn.COST],
        meta: { narrow: true, end: true },
        enableSorting: true,
        cell: info => formatCost(info.getValue()),
      }),
      column.accessor('timeSpent', {
        id: VehicleColumn.TIME_SPENT,
        header: labels[VehicleColumn.TIME_SPENT],
        meta: { narrow: true, end: true },
        enableSorting: true,
        cell: info => formatTimeSpent(info.getValue()),
      }),
      column.accessor('notificationTime', {
        id: VehicleColumn.NOTIFICATION_TIME,
        header: labels[VehicleColumn.NOTIFICATION_TIME],
        meta: { narrow: true },
        cell(info) {
          const { scheduledStart } = info.row.original
          const notificationTime = info.getValue()
          if (!notificationTime || !scheduledStart) return '-'

          return formatDistanceStrict(
            new Date(scheduledStart),
            new Date(notificationTime)
          )
        },
      }),
      column.accessor('responsibleUser', {
        id: VehicleColumn.RESPONSIBLE_USER,
        header: labels[VehicleColumn.RESPONSIBLE_USER],
        meta: { narrow: true },
        enableSorting: true,
        sortingFn: (a, b) =>
          (a.original.responsibleUser?.fullName ?? '').localeCompare(
            b.original.responsibleUser?.fullName ?? ''
          ),
        cell: info => info.getValue()?.fullName || '-',
      }),
      column.display({
        id: MENU_COLUMN_ID,
        header: '',
        meta: { narrow: true, center: true },
        cell(info) {
          const servicePlan = info.row.original
          // An integration rewrites its own rows on every sync, so editing,
          // deleting or moving one only holds until the next run.
          const fromIntegration = servicePlan.source !== 'MANUAL'

          const items: TableMenuItem[] = []
          if (canEdit && !fromIntegration) {
            items.push({
              id: 'edit',
              type: 'regular',
              label: translations.edit,
              icon: 'edit',
              onClick: () => onEdit(servicePlan),
            })
          }
          if (canAdd && canDelete && !fromIntegration) {
            items.push({
              id: 'move-to-expirations',
              type: 'regular',
              label: translations.moveToExpirations,
              icon: 'calendar-day',
              onClick: () => onMoveToExpirations(servicePlan),
            })
          }
          if (canDelete && !fromIntegration) {
            items.push({
              id: 'delete',
              type: 'regular',
              label: translations.delete,
              icon: 'trash',
              onClick: () => onDelete(servicePlan),
            })
          }
          if (items.length === 0) return null

          return <TableMenu items={items} />
        },
      }),
    ]

    return showVehicle
      ? defs
      : defs.filter(def => def.id !== VehicleColumn.VEHICLE)
  }, [
    canAdd,
    canDelete,
    canEdit,
    column,
    labels,
    onDelete,
    onEdit,
    onMoveToExpirations,
    showVehicle,
    translations.delete,
    translations.edit,
    translations.moveToExpirations,
  ])

  const columns = useMemo(
    () => resolveVehicleColumns(columnDefs, config),
    [columnDefs, config]
  )

  return (
    <DataTable
      className={cn('w-full')}
      columns={columns}
      data={servicePlans}
      loading={loading || configLoading}
      inCard
      initialSort={[{ id: VehicleColumn.SCHEDULED_START, desc: false }]}
      noDataText={translations.noServices}
      pagination={{ itemsText: n => translations.items({ n }) }}
      rowClassName={row =>
        row.status === 'CANCELLED' ? 'text-stone-400 line-through' : undefined
      }
    />
  )
}
